import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { Box, IconButton } from "@mui/material";
import { useOpenUrl } from "@src/mobile/utils/openUrl";
import { useCallback, useState } from "react";

import { Text } from "../../components/Text";
import { useTu } from "@src/appV2/i18n/useTu";

interface ChatFileAttachmentProps {
  fileName: string;
  fileSize: number;
  fileType: string;
  fileUrl: string;
  thumbnailUrl?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Display a file attachment in a chat message.
 *
 * Images are rendered inline and can be clicked to open a full-size preview.
 * Other file types show a download card with filename, size, and a download button.
 */
export function ChatFileAttachment(props: ChatFileAttachmentProps) {
  const { fileName, fileSize, fileType, fileUrl, thumbnailUrl } = props;

  const isImage = fileType.startsWith("image/");

  if (isImage) {
    return <ImageAttachment fileName={fileName} fileUrl={fileUrl} thumbnailUrl={thumbnailUrl} />;
  }

  return <FileAttachmentCard fileName={fileName} fileSize={fileSize} fileUrl={fileUrl} />;
}

interface ImageAttachmentProps {
  fileName: string;
  fileUrl: string;
  thumbnailUrl?: string;
}

/**
 * Renders an image inline with click-to-preview lightbox.
 * Uses a fixed-position overlay instead of MUI Dialog to avoid restricted import.
 */
function ImageAttachment(props: ImageAttachmentProps) {
  const { tu } = useTu("chat");
  const { fileName, fileUrl, thumbnailUrl } = props;
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const closePreview = useCallback(() => {
    setIsPreviewOpen(false);
  }, []);

  return (
    <>
      <Box
        aria-label={`Image: ${fileName}`}
        component="button"
        sx={(theme) => ({
          maxWidth: "100%",
          maxHeight: 300,
          borderRadius: theme.borderRadius?.medium,
          cursor: "pointer",
          display: "block",
          padding: 0,
          border: "none",
          background: "none",
        })}
        onClick={() => {
          setIsPreviewOpen(true);
        }}
      >
        <Box
          alt={fileName}
          component="img"
          src={thumbnailUrl ?? fileUrl}
          sx={(theme) => ({
            maxWidth: "100%",
            maxHeight: 300,
            borderRadius: theme.borderRadius?.medium,
            display: "block",
          })}
        />
      </Box>
      {isPreviewOpen && (
        <Box
          aria-label={tu("Close image preview")}
          aria-modal="true"
          role="dialog"
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            cursor: "pointer",
          }}
          onClick={closePreview}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === "Escape") {
              closePreview();
            }
          }}
        >
          <Box
            alt={fileName}
            component="img"
            src={fileUrl}
            sx={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
        </Box>
      )}
    </>
  );
}

interface FileAttachmentCardProps {
  fileName: string;
  fileSize: number;
  fileUrl: string;
}

/**
 * Renders a non-image file as a download card with icon, name, size, and download button.
 */
function FileAttachmentCard(props: FileAttachmentCardProps) {
  const { tu } = useTu("chat");
  const { fileName, fileSize, fileUrl } = props;
  const openUrl = useOpenUrl();

  const handleDownload = useCallback(() => {
    void openUrl(fileUrl);
  }, [fileUrl, openUrl]);

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        padding: 3,
        borderRadius: theme.borderRadius?.medium,
        backgroundColor: theme.palette.background.default,
        width: "100%",
        minWidth: 0,
      })}
    >
      <Box
        sx={(theme) => ({
          width: "48px",
          height: "48px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: theme.palette.base?.inactive,
          borderRadius: 1,
        })}
      >
        <InsertDriveFileIcon sx={{ fontSize: "24px", color: "text.secondary" }} />
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Text
          semibold
          variant="body2"
          sx={{
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {fileName}
        </Text>
        <Text
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
          })}
        >
          {formatFileSize(fileSize)}
        </Text>
      </Box>

      <IconButton
        aria-label={tu("Download file")}
        size="small"
        sx={(theme) => ({
          color: theme.palette.text.secondary,
          "&:hover": {
            color: theme.palette.text.primary,
          },
        })}
        onClick={handleDownload}
      >
        <DownloadIcon sx={{ fontSize: "18px" }} />
      </IconButton>
    </Box>
  );
}
